/**
 * EmbedPlayPage — встраиваемая версия теста
 * apps/web/src/pages/embed/EmbedPlayPage.tsx
 * 
 * Переиспользует CollectionPlayPage, но:
 * - Кнопки "Продолжить"/"К модулям" → embed-роуты
 * - Шаринг → ссылка на бот (не внутренняя навигация)
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useCollectionsStore } from '../../stores/collections';
import { answerQuestion as apiAnswerQuestion } from '../../lib/collections-api';
import { coursesBotUrl } from '../../lib/bots';

// Стили анимаций (копируем из основного)
const animationStyles = `
  @keyframes fadeInUp { from { opacity: 0; transform: translateY(15px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes scaleIn { from { opacity: 0; transform: scale(0.9); } to { opacity: 1; transform: scale(1); } }
  @keyframes bounce { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } }
  @keyframes shake { 0%, 100% { transform: translateX(0); } 25% { transform: translateX(-4px); } 75% { transform: translateX(4px); } }
  @keyframes confetti { 0% { transform: translateY(0) rotate(0deg); opacity: 1; } 100% { transform: translateY(-80px) rotate(720deg); opacity: 0; } }
  @keyframes streakFire { 0%, 100% { transform: scale(1) rotate(-3deg); } 50% { transform: scale(1.15) rotate(3deg); } }
  .animate-fadeInUp { animation: fadeInUp 0.3s ease-out; }
  .animate-scaleIn { animation: scaleIn 0.25s ease-out; }
  .animate-bounce { animation: bounce 0.35s ease-out; }
  .animate-shake { animation: shake 0.35s ease-out; }
  .animate-streakFire { animation: streakFire 0.4s ease-in-out infinite; }
`;

export default function EmbedPlayPage() {
  const { id, moduleId } = useParams<{ id: string; moduleId: string }>();
  const navigate = useNavigate();
  const startedRef = useRef(false);

  const session = useCollectionsStore((state) => state.session);
  const result = useCollectionsStore((state) => state.result);
  const isLoading = useCollectionsStore((state) => state.isLoading);
  const error = useCollectionsStore((state) => state.error);
  const startModule = useCollectionsStore((state) => state.startModule);
  const answerQuestion = useCollectionsStore((state) => state.answerQuestion);
  const resetGame = useCollectionsStore((state) => state.resetGame);

  const [selectedAnswer, setSelectedAnswer] = useState<number | null>(null);
  const [showResult, setShowResult] = useState(false);
  const [correctIndex, setCorrectIndex] = useState<number | null>(null);
  const [explanation, setExplanation] = useState<string | undefined>();
  const [isCorrect, setIsCorrect] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [streak, setStreak] = useState(0);
  const [maxStreak, setMaxStreak] = useState(0);
  const [xpEarned, setXpEarned] = useState(0);

  const currentQuestion = session?.questions?.[session.currentIndex] || null;
  const progress = session
    ? { current: session.currentIndex + 1, total: session.questions.length }
    : { current: 0, total: 0 };

  useEffect(() => {
    if (id && moduleId && !startedRef.current) {
      startedRef.current = true;
      startModule(id, moduleId);
    }
    return () => { resetGame(); };
  }, [id, moduleId]);

  const handleAnswer = async (answerIndex: number) => {
    if (showResult || !currentQuestion || !session || !id || !moduleId) return;
    setSelectedAnswer(answerIndex);

    try {
      const res = await apiAnswerQuestion(id, moduleId, session.id, currentQuestion.id, answerIndex);
      setCorrectIndex(res.correctIndex);
      setExplanation(res.explanation);
      setIsCorrect(res.correct);
      setShowResult(true);

      if (res.correct) {
        const s = streak + 1;
        setStreak(s);
        setMaxStreak(Math.max(maxStreak, s));
        setXpEarned(prev => prev + 10 + Math.min(s - 1, 5) * 2);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 800);
      } else {
        setStreak(0);
      }
    } catch {
      answerQuestion(answerIndex);
    }
  };

  const handleNext = useCallback(() => {
    answerQuestion(selectedAnswer || 0);
    setSelectedAnswer(null);
    setShowResult(false);
    setCorrectIndex(null);
    setExplanation(undefined);
    setIsCorrect(false);
  }, [answerQuestion, selectedAnswer]);

  // Embed-навигация
  const handleContinue = () => navigate(`/embed/collection/${id}`);
  const handleRetry = () => {
    startedRef.current = false;
    setStreak(0); setMaxStreak(0); setXpEarned(0);
    resetGame();
    setTimeout(() => {
      if (id && moduleId) { startedRef.current = true; startModule(id, moduleId); }
    }, 100);
  };
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const handleExit = () => setShowExitConfirm(true);
  const handleExitConfirm = () => {
    setShowExitConfirm(false);
    resetGame();
    navigate(`/embed/collection/${id}`);
  };

  // Embed шаринг — всегда ссылка на бот
  const handleShare = () => {
    const url = id
      ? coursesBotUrl(`collection_${id}`)
      : coursesBotUrl();
    const score = result?.score || 0;
    const text = `🎉 Я прошёл тест на ${score}%!\nПопробуй тоже!`;

    if (navigator.share) {
      navigator.share({ title: 'BESHEK Quiz', text, url }).catch(() => {});
    } else if (navigator.clipboard) {
      navigator.clipboard.writeText(`${text}\n${url}`);
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Скопировано в буфер обмена!' } }));
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center">
        <style>{animationStyles}</style>
        <div className="w-14 h-14 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-6 text-center max-w-sm w-full">
          <div className="text-5xl mb-3">😕</div>
          <p className="text-gray-500 text-sm mb-4">{error}</p>
          <button onClick={handleContinue} className="w-full py-2 bg-gray-900 text-white font-semibold rounded-lg">Назад</button>
        </div>
      </div>
    );
  }

  // Результат
  if (result) {
    const passed = result.passed;
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex flex-col items-center justify-center p-4">
        <style>{animationStyles}</style>
        <div className="w-full max-w-sm bg-white rounded-2xl p-5 shadow-2xl animate-scaleIn">
          <div className="text-center mb-4">
            <div className="text-6xl mb-2 animate-bounce">{result.collectionCompleted ? '🏆' : passed ? '🎉' : '💪'}</div>
            <div className={`text-5xl font-black mb-1 ${passed ? 'text-green-500' : 'text-orange-500'}`}>{result.score}%</div>
            <p className="text-gray-500 text-sm">{result.correctCount} из {result.totalCount} правильных</p>
          </div>

          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-yellow-50 rounded-xl p-3 text-center">
              <div className="text-xl">⭐</div>
              <div className="text-xl font-bold text-yellow-600">+{xpEarned || result.xpEarned}</div>
              <div className="text-xs text-yellow-700">XP</div>
            </div>
            <div className="bg-orange-50 rounded-xl p-3 text-center">
              <div className="text-xl">🔥</div>
              <div className="text-xl font-bold text-orange-600">{maxStreak}</div>
              <div className="text-xs text-orange-700">макс. серия</div>
            </div>
          </div>

          <div className="space-y-2">
            {passed ? (
              <button onClick={handleContinue} className="w-full py-3 bg-green-500 hover:bg-green-600 text-white font-bold rounded-xl">
                {result.collectionCompleted ? '🏠 К курсу' : '➡️ Продолжить'}
              </button>
            ) : (
              <button onClick={handleRetry} className="w-full py-3 bg-blue-500 hover:bg-blue-600 text-white font-bold rounded-xl">
                🔄 Попробовать снова
              </button>
            )}
            <button onClick={handleShare} className="w-full py-3 bg-purple-500 hover:bg-purple-600 text-white font-semibold rounded-xl">
              📤 Поделиться
            </button>
            <button onClick={handleContinue} className="w-full py-2 bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm rounded-xl">
              К модулям
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!currentQuestion || !session) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-500 flex items-center justify-center">
        <div className="w-14 h-14 border-4 border-white border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Игровой экран
  const percent = progress.total > 0 ? (progress.current / progress.total) * 100 : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-purple-50 flex flex-col">
      <style>{animationStyles}</style>

      {showExitConfirm && (
        <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-2xl p-6 mx-4 max-w-sm w-full shadow-xl">
            <p className="text-gray-900 text-center mb-6">Выйти? Прогресс не сохранится.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowExitConfirm(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-700 font-medium">Отмена</button>
              <button onClick={handleExitConfirm} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-medium">Выйти</button>
            </div>
          </div>
        </div>
      )}

      {/* Confetti */}
      {showConfetti && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden">
          {Array.from({ length: 15 }, (_, i) => (
            <div key={i} className="absolute w-2 h-2 rounded-full"
              style={{
                left: `${Math.random() * 100}%`, top: '40%',
                backgroundColor: ['#FFD700','#FF6B6B','#4ECDC4','#45B7D1','#96CEB4'][i % 5],
                animation: `confetti 0.8s ease-out ${Math.random() * 0.2}s forwards`,
              }} />
          ))}
        </div>
      )}

      {/* Progress Header */}
      <div className="bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-500 px-3 py-2 text-white">
        <div className="flex items-center justify-between mb-2">
          <button onClick={handleExit} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/20 hover:bg-white/30">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div className="flex items-center gap-1 bg-white/20 rounded-full px-3 py-1">
            <span className="text-sm font-medium">{progress.current}</span>
            <span className="text-white/60">/</span>
            <span className="text-sm text-white/80">{progress.total}</span>
          </div>
          <div className="flex items-center gap-2">
            {streak > 0 && (
              <div className={`flex items-center gap-1 bg-orange-500/90 rounded-full px-2 py-0.5 ${streak >= 3 ? 'animate-streakFire' : ''}`}>
                <span>🔥</span><span className="text-xs font-bold">{streak}</span>
              </div>
            )}
            {xpEarned > 0 && (
              <div className="flex items-center gap-1 bg-yellow-500/90 rounded-full px-2 py-0.5">
                <span className="text-xs">⭐</span><span className="text-xs font-bold">+{xpEarned}</span>
              </div>
            )}
          </div>
        </div>
        <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
          <div className="h-full bg-white rounded-full transition-all duration-500" style={{ width: `${percent}%` }} />
        </div>
      </div>

      {/* Question */}
      <div key={`q-${session.currentIndex}`} className="flex-1 flex flex-col p-3 animate-fadeInUp">
        <div className="bg-white rounded-2xl p-4 mb-2 shadow-md">
          <h2 className="text-base font-bold text-gray-900 leading-snug">{currentQuestion.text}</h2>
        </div>
        <div className="space-y-2">
          {currentQuestion.options.map((option: string, index: number) => {
            const isSelected = selectedAnswer === index;
            const isCorrectOpt = correctIndex === index;
            const isWrong = showResult && isSelected && !isCorrectOpt;

            let style = 'bg-white border-2 border-gray-100 hover:border-purple-300';
            let iconBg = 'bg-gray-100 text-gray-600';
            let anim = '';

            if (showResult) {
              if (isCorrectOpt) { style = 'bg-green-50 border-2 border-green-400'; iconBg = 'bg-green-500 text-white'; anim = 'animate-bounce'; }
              else if (isWrong) { style = 'bg-red-50 border-2 border-red-400'; iconBg = 'bg-red-500 text-white'; anim = 'animate-shake'; }
              else { style = 'bg-gray-50 border-2 border-gray-100 opacity-50'; }
            } else if (isSelected) { style = 'bg-purple-50 border-2 border-purple-400'; iconBg = 'bg-purple-500 text-white'; }

            return (
              <button key={index} onClick={() => !showResult && handleAnswer(index)} disabled={showResult}
                className={`w-full p-3 rounded-xl text-left transition-all ${style} ${anim}`}>
                <div className="flex items-center gap-3">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold ${iconBg}`}>
                    {showResult && isCorrectOpt ? '✓' : showResult && isWrong ? '✗' : String.fromCharCode(65 + index)}
                  </div>
                  <span className="flex-1 text-sm text-gray-800">{option}</span>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Explanation */}
      {showResult && explanation && (
        <div className="mx-3 mb-3 animate-fadeInUp">
          <div className={`p-3 rounded-xl border ${isCorrect ? 'bg-green-50 border-green-200' : 'bg-blue-50 border-blue-200'}`}>
            <div className="flex items-start gap-2 mb-3">
              <span className="text-lg">{isCorrect ? '🎯' : '💡'}</span>
              <p className={`text-sm ${isCorrect ? 'text-green-700' : 'text-blue-700'}`}>{explanation}</p>
            </div>
            <button onClick={handleNext}
              className={`w-full py-2.5 rounded-lg font-semibold text-white ${isCorrect ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-500 hover:bg-blue-600'}`}>
              Далее →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
